<script lang="ts">
  import Modal from '@shared/ui/Modal.svelte';
  import type { SalesAction } from '@shared/types/sales-action';

  interface SolutionOption {
    id: string;
    label: string;
  }

  interface Props {
    open: boolean;
    action: SalesAction;
    solutions: SolutionOption[];
    /// 同じターゲットで既に open 商談行があるソリューション (disabled 表示 = 既存商談あり)。
    existingOpenSolutionIds: string[];
    /// 選択ソリューション群 + メモコピー可否で確定。
    onConfirm: (solutionIds: string[], copyNote: boolean) => void;
    onClose: () => void;
  }

  let {
    open,
    action,
    solutions,
    existingOpenSolutionIds,
    onConfirm,
    onClose,
  }: Props = $props();

  // 元行の product_id は候補から除外する。
  const options = $derived(solutions.filter((s) => s.id !== action.product_id));
  function isExisting(id: string): boolean {
    return existingOpenSolutionIds.includes(id);
  }

  let selected = $state<string[]>([]);
  let copyNote = $state(false);

  function toggle(id: string) {
    if (isExisting(id)) return;
    selected = selected.includes(id)
      ? selected.filter((x) => x !== id)
      : [...selected, id];
  }

  const canConfirm = $derived(selected.length >= 1);
</script>

<Modal {open} title="ソリューションを追加" onClose={onClose}>
  <div class="body">
    <p>
      <strong>{action.action_title ?? '(無題)'}</strong>
      と同じ商談条件で、選択したソリューションごとに新しい商談行を作成します（1ソリューション1行）。
    </p>
    {#if options.length === 0}
      <p class="muted">追加できるソリューションがありません。</p>
    {:else}
      <fieldset>
        <legend>追加するソリューション <em>*</em></legend>
        <div class="checks">
          {#each options as o (o.id)}
            <label class="check" class:disabled={isExisting(o.id)}>
              <input
                type="checkbox"
                checked={selected.includes(o.id)}
                disabled={isExisting(o.id)}
                onchange={() => toggle(o.id)}
              />
              <span>
                {o.label}{#if isExisting(o.id)}
                  <span class="tag">既存商談あり</span>
                {/if}
              </span>
            </label>
          {/each}
        </div>
      </fieldset>
    {/if}

    <label class="check copy-note">
      <input type="checkbox" bind:checked={copyNote} />
      <span>メモをコピー</span>
    </label>

    {#if !canConfirm}
      <p class="warn">追加するソリューションを 1 件以上選択してください。</p>
    {/if}
  </div>
  {#snippet footer()}
    <button type="button" onclick={onClose}>キャンセル</button>
    <button
      type="button"
      class="primary"
      data-testid="add-solution-confirm"
      disabled={!canConfirm}
      onclick={() => onConfirm(selected, copyNote)}
    >
      追加する
    </button>
  {/snippet}
</Modal>

<style>
  .body {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .body p {
    margin: 0;
  }
  fieldset {
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    padding: 8px 10px;
    margin: 0;
  }
  legend {
    font-size: 0.8rem;
    color: #374151;
    padding: 0 4px;
  }
  legend em {
    color: #b91c1c;
    font-style: normal;
  }
  .checks {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .check {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 6px;
    font-size: 0.875rem;
  }
  .check.disabled {
    color: #9ca3af;
  }
  .copy-note {
    margin-top: 4px;
  }
  .tag {
    display: inline-block;
    margin-left: 4px;
    padding: 0 6px;
    font-size: 0.7rem;
    background: #f3f4f6;
    color: #6b7280;
    border-radius: 999px;
  }
  .muted {
    color: #6b7280;
    font-size: 0.875rem;
  }
  .warn {
    color: #b45309;
    font-size: 0.875rem;
  }
  button {
    padding: 8px 16px;
    border: 1px solid #d1d5db;
    background: white;
    border-radius: 4px;
    cursor: pointer;
    font: inherit;
  }
  button.primary {
    background: #2563eb;
    border-color: #2563eb;
    color: white;
  }
  button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
</style>
